/**
 * Collection Workflow Hook
 *
 * Manages state and logic for the collection request workflow
 * Uses the same pattern as notification workflow with collection-specific filtering
 */

import { useMemo } from 'react';
import {
  useWorkflowData,
  useCreateBulkDrafts,
  useSendCorrespondences
} from '@/lib/hooks/use-workflow-data';
import WorkflowDataService from '@/lib/services/correspondences/workflows/workflow-data.service';
import { transformInstitution } from '@/lib/services/correspondences/workflows/transformers';
import { toast } from '@/lib/toast';
import type {
  BackendCorrespondence,
  WorkflowInstitution
} from '@/lib/services/correspondences/workflows/workflow-types';

interface SendInstitutionData {
  id: number | string;
  letterContent?: string;
  emailSubject?: string;
  emailBody?: string;
  emailTo?: string;
  signatoryUserId?: number | null;
  /** File IDs to attach as additional documents */
  attachments?: number[];
}

export interface SendDataPayload {
  institutions: Array<Pick<WorkflowInstitution, 'id' | 'emailAddress'>>;
  sendData?: {
    institutions?: SendInstitutionData[];
    sendSenderCopy?: boolean;
  };
}

interface CorrespondenceUpdates {
  correspondenceLetterBody?: string;
  subject?: string;
  mainContent?: string;
  emailto?: string;
  signatoryUser?: { id: number } | null;
  requiresEmailResearch?: boolean;
}

/**
 * Main collection workflow hook
 * Uses the same shared infrastructure as notification workflow
 */
export function useCollectionWorkflow(
  matterId: number,
  institutionId?: number
) {
  // Fetch workflow data using the shared hook with collection type
  const {
    data: workflowData,
    isLoading,
    error,
    refetch
  } = useWorkflowData(matterId, 'collection-request');

  // Backend now handles filtering with correspondenceType=collection-request
  // We also merge in institutions from pending-research-email correspondences
  // that the backend may have filtered out of the institutions list
  const filteredData = useMemo(() => {
    if (!workflowData) return null;

    let institutions = [...workflowData.institutions];

    // Merge institutions from pending-research-email correspondences
    const existingIds = new Set(institutions.map((inst) => inst.id));
    const pendingResearchCorrespondences = (
      workflowData.correspondences || []
    ).filter(
      (c) =>
        c.type === 'collection-request' &&
        c.status === 'pending-research-email' &&
        c.institution &&
        !existingIds.has(c.institution.id)
    );
    for (const corr of pendingResearchCorrespondences) {
      institutions.push(transformInstitution(corr.institution, [], []));
      existingIds.add(corr.institution.id);
    }

    // If specific institution ID provided, filter to just that one
    if (institutionId) {
      institutions = institutions.filter((inst) => inst.id === institutionId);
    }

    return {
      ...workflowData,
      institutions
    };
  }, [workflowData, institutionId]);

  // Validation logic for collection requests
  const validation = useMemo(() => {
    if (!filteredData) {
      return {
        canProceed: false,
        blockingErrors: isLoading ? ['Loading workflow data...'] : [],
        warnings: []
      };
    }

    const blockingErrors: string[] = [];
    const warnings: string[] = [];

    // Check for institutions
    if (filteredData.institutions.length === 0) {
      blockingErrors.push('No institutions available for collection requests');
    }

    // Check for Grant of Probate or Letters of Administration
    const hasGrantOfProbate = filteredData.availableDocuments.some(
      (doc) => doc.fileType === 'grant-of-probate' && doc.isCertifiedCopy
    );
    if (!hasGrantOfProbate) {
      warnings.push(
        'No certified Grant of Probate or Letters of Administration uploaded'
      );
    }

    // Check for signatories
    if (filteredData.availableSignatories.length === 0) {
      blockingErrors.push('No signatories available');
    } else {
      const hasSignatureOnFile = filteredData.availableSignatories.some(
        (s) => s.hasSignature
      );
      if (!hasSignatureOnFile) {
        warnings.push('No signatory has a signature on file');
      }
    }

    // Check for law firm letterhead
    if (!filteredData.lawFirm?.hasLetterhead) {
      warnings.push('Law firm letterhead not configured');
    }

    return {
      canProceed: blockingErrors.length === 0,
      blockingErrors,
      warnings
    };
  }, [filteredData, isLoading]);

  // Mutations
  const createDrafts = useCreateBulkDrafts();
  const sendCorrespondences = useSendCorrespondences(matterId);

  // Send collection requests handler
  const sendCollectionRequests = async (sendData: SendDataPayload) => {
    try {
      // Get the current correspondences (should already exist from component mount)
      const currentCorrespondences: BackendCorrespondence[] =
        filteredData?.correspondences || [];

      // Get institution IDs for filtering
      const institutionIds = sendData.institutions.map((inst) =>
        typeof inst.id === 'string' ? parseInt(inst.id) : inst.id
      );

      // Verify correspondences exist (they should have been created on component mount)
      const missingCorrespondences = institutionIds.filter(
        (instId: number) =>
          !currentCorrespondences.some(
            (c) =>
              c.institution?.id === instId && c.type === 'collection-request'
          )
      );

      if (missingCorrespondences.length > 0) {
        throw new Error(
          `Correspondences not found for institutions: ${missingCorrespondences.join(', ')}`
        );
      }

      // Use the existing correspondences
      const freshCorrespondences = currentCorrespondences;

      // Step 2: Update each correspondence if user made edits (letter content, email, subject, signatory)
      if (sendData.sendData?.institutions) {
        for (const instData of sendData.sendData.institutions) {
          const instId =
            typeof instData.id === 'string'
              ? parseInt(instData.id)
              : instData.id;

          // Find the correspondence from the fresh data
          // Must filter by type to avoid updating the wrong correspondence
          const correspondence = freshCorrespondences.find(
            (c) =>
              c.institution?.id === instId && c.type === 'collection-request'
          );
          const correspondenceId = correspondence?.id;

          if (correspondenceId) {
            const updates: CorrespondenceUpdates = {};

            if (instData.letterContent) {
              updates.correspondenceLetterBody = instData.letterContent;
            }
            if (instData.emailSubject) {
              updates.subject = instData.emailSubject;
            }
            if (instData.emailBody) {
              updates.mainContent = instData.emailBody;
            }
            if (instData.emailTo) {
              updates.emailto = instData.emailTo;
            }
            if (instData.signatoryUserId !== undefined) {
              updates.signatoryUser = instData.signatoryUserId
                ? { id: instData.signatoryUserId }
                : null;
            }

            // Flag for email research if institution has no email and user didn't provide one
            const institution = sendData.institutions.find((i) => {
              const iId = typeof i.id === 'string' ? parseInt(i.id) : i.id;
              return iId === instId;
            });
            if (institution && !institution.emailAddress && !instData.emailTo) {
              updates.requiresEmailResearch = true;
            }

            // Only call update if there are changes
            if (Object.keys(updates).length > 0) {
              await WorkflowDataService.updateDraft(
                matterId,
                correspondenceId,
                updates as unknown as Parameters<
                  typeof WorkflowDataService.updateDraft
                >[2]
              );
            }
          }
        }
      }

      // Step 3: Send all correspondences using fresh correspondence IDs
      const correspondenceIds = freshCorrespondences
        .filter((c) => {
          // Only send collection-request correspondences for the institutions we selected
          const institutionId = c.institution?.id;
          if (institutionId === undefined) return false;
          return (
            institutionIds.includes(institutionId) &&
            c.type === 'collection-request'
          );
        })
        .map((c) => c.id)
        .filter((id): id is number => id !== undefined && id !== null);

      const sendSenderCopy = sendData.sendData?.sendSenderCopy || false;

      const results = await sendCorrespondences.mutateAsync({
        correspondenceIds,
        sendSenderCopy
      });

      // Show toast if any institutions required email research (no manual email provided)
      const hadEmailResearch = (sendData.sendData?.institutions || []).some(
        (instData) => {
          const institution = sendData.institutions.find((i) => {
            const iId = typeof i.id === 'string' ? parseInt(i.id) : i.id;
            const instId =
              typeof instData.id === 'string'
                ? parseInt(instData.id)
                : instData.id;
            return iId === instId;
          });
          return institution && !institution.emailAddress && !instData.emailTo;
        }
      );
      if (hadEmailResearch) {
        toast.info(
          'Request submitted. We will locate details and notify shortly.'
        );
      }

      return results;
    } catch (error) {
      console.error('Failed to send collection requests:', error);
      throw error;
    }
  };

  // Transform send results to match component expectations
  const sendResults = useMemo(() => {
    if (!sendCorrespondences.data) return undefined;

    const { results } = sendCorrespondences.data;
    const successful = results
      .filter((r) => r.success && r.correspondenceId)
      .map((r) => r.correspondenceId!);
    const failed = results
      .filter((r) => !r.success)
      .map((r) => ({
        id: r.institutionId,
        error: r.error || 'Send failed'
      }));

    return { successful, failed };
  }, [sendCorrespondences.data]);

  // Return data directly without transformation
  return {
    data: filteredData
      ? {
          institutions: filteredData.institutions,
          documents: filteredData.availableDocuments,
          signatories: filteredData.availableSignatories,
          correspondences: filteredData.correspondences,
          matter: filteredData.matter,
          lawFirm: filteredData.lawFirm
        }
      : undefined,
    isLoading,
    error,
    validation,
    sendCollectionRequests,
    refetchWorkflowData: refetch,
    isSending: createDrafts.isPending || sendCorrespondences.isPending,
    sendError: createDrafts.error || sendCorrespondences.error,
    sendResults
  };
}
